<script setup lang="ts">
import { ref, watch } from 'vue';
import ReviewForm, { type ReviewFormValues } from './ReviewForm.vue';
import type { ExpertData } from './ExpertDetailsCard.vue';
import { useUpdateClientReview } from '@/app/composables/reviews/useUpdateClientReview';

const props = withDefaults(defineProps<{
    reviewId: number;
    expert: ExpertData;
    initialValues: {
        quality: number;
        communication: number;
        timelines: number;
        value_for_money: number;
        review_text: string;
        is_anonymous?: boolean;
    };
    showCloseButton?: boolean;
}>(), {
    showCloseButton: true,
});

const emit = defineEmits<{
    success: [];
    cancel: [];
}>();

const formRef = ref<InstanceType<typeof ReviewForm> | null>(null);

// Update review mutation
const { updateReview, isUpdating, isSuccess, reset } = useUpdateClientReview();

// Handle form submission
const handleSubmit = async (values: ReviewFormValues) => {
    try {
        await updateReview(props.reviewId, {
            quality: values.quality,
            communication: values.communication,
            timelines: values.timelines,
            value_for_money: values.value_for_money,
            review_text: values.review_text,
            is_anonymous: values.is_anonymous,
        });
    } catch (error) {
        console.error('[EditReviewForm] Submit error:', error);
    }
};

// Watch for success and emit after a brief delay
watch(isSuccess, (newValue) => {
    if (newValue) {
        setTimeout(() => {
            emit('success');
        }, 1500);
    }
});

const handleCancel = () => {
    emit('cancel');
};

// Reset form
const resetForm = () => {
    formRef.value?.resetForm();
    reset();
};

defineExpose({ resetForm });
</script>

<template>
    <ReviewForm
        ref="formRef"
        :key="reviewId"
        :expert="expert"
        :show-close-button="showCloseButton"
        :is-submitting="isUpdating"
        :is-success="isSuccess"
        :initial-values="initialValues"
        :review-text-max-length="5000"
        title="Edit your **review**"
        description="Update your rating and review to reflect your experience with this expert."
        submit-label="Update Review"
        @submit="handleSubmit"
        @cancel="handleCancel"
    />
</template>
